// src/modules/projects/calc/Eco7KitchenCalc.jsx
import { useState, useRef } from "react";
import { C } from "../../../constants";
import { Ic } from "../../../components/ui/Icons";
import { inputStyle, selectStyle, inputFocus, inputBlur } from "../../../components/ui/Components";
import { generateEco7BOM } from "./eco7Engine";
import { generateEco7Report, generateEco7ReportBase64 } from "./eco7ReportGenerator";

function Label({ en, es, required }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.t1 }}>
        {en}{required && <span style={{ color: C.red, marginLeft: 3 }}>*</span>}
      </span>
      {es && <span style={{ display: "block", fontSize: 11.5, color: C.t3, fontStyle: "italic" }}>{es}</span>}
    </div>
  );
}
function Section({ title, titleEs }) {
  return (
    <div style={{ borderBottom: `2px solid ${C.navy}15`, paddingBottom: 8, marginTop: 32, marginBottom: 20 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: C.navy, margin: 0 }}>{title}</h3>
      {titleEs && <span style={{ fontSize: 12, color: C.t3, fontStyle: "italic" }}>{titleEs}</span>}
    </div>
  );
}
function TF({ en, es, value, onChange, placeholder, type = "text", required, error, disabled, rows }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <Label en={en} es={es} required={required} />
      {rows ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows}
          style={{ ...inputStyle, resize: "vertical", minHeight: 70, borderColor: error ? C.red : undefined }}
          onFocus={inputFocus} onBlur={inputBlur} />
      ) : (
        <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          disabled={disabled}
          style={{ ...inputStyle, borderColor: error ? C.red : undefined, background: disabled ? C.grayBg : undefined }}
          onFocus={inputFocus} onBlur={inputBlur} />
      )}
      {error && <span style={{ fontSize: 11, color: C.red, marginTop: 2, display: "block" }}>{error}</span>}
    </div>
  );
}
function SF({ en, es, value, onChange, options, required, error, disabled, note }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <Label en={en} es={es} required={required} />
      <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
        style={{ ...selectStyle, borderColor: error ? C.red : undefined, background: disabled ? C.grayBg : undefined, color: disabled ? C.t3 : undefined, opacity: disabled ? 0.6 : 1 }}
        onFocus={inputFocus} onBlur={inputBlur}>
        <option value="">-- Select --</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      {note && <span style={{ fontSize: 11, color: C.t3, marginTop: 2, display: "block", fontStyle: "italic" }}>{note}</span>}
      {error && <span style={{ fontSize: 11, color: C.red, marginTop: 2, display: "block" }}>{error}</span>}
    </div>
  );
}
function UnitSelector({ value, onChange, required, error }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <Label en="Unit System" es="Sistema de Unidades" required={required} />
      <div style={{ display: "flex", gap: 8 }}>
        {["Metric (m, m², °C)", "Imperial (ft, ft², °F)"].map(o => {
          const s = value === o;
          return (
            <button key={o} type="button" onClick={() => onChange(o)}
              style={{ padding: "8px 18px", borderRadius: 8, border: `1.5px solid ${s ? C.navy : error ? C.red : C.border}`, background: s ? `${C.navy}10` : C.white, color: s ? C.navy : C.t2, fontSize: 13, cursor: "pointer", fontFamily: "inherit", fontWeight: s ? 600 : 400, flex: 1, textAlign: "center" }}>
              {o}
            </button>
          );
        })}
      </div>
      {error && <span style={{ fontSize: 11, color: C.red, marginTop: 4, display: "block" }}>{error}</span>}
    </div>
  );
}

function KitchenBOMTable({ r }) {
  return (
    <div style={{ marginBottom: 16, background: C.grayBg, borderRadius: 10, padding: "12px 16px", border: `1px solid ${C.border}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
        <h4 style={{ fontSize: 14, fontWeight: 700, color: C.navy, margin: 0 }}>{r.name}</h4>
        <div style={{ display: "flex", gap: 12, fontSize: 12, color: C.t2 }}>
          <span>Eco7: <strong style={{ color: C.navy }}>{r.agentLiters} L</strong></span>
          <span>Cylinder: <strong style={{ color: C.navy }}>{r.cylLabel}</strong></span>
          <span>Cook: <strong>{r.cookNozzles} nozzles</strong></span>
          {(r.duct1Nozzles + r.duct2Nozzles) > 0 && <span>Duct: <strong>{r.duct1Nozzles + r.duct2Nozzles} nozzles</strong></span>}
          {r.tubingMeters > 0 && <span>Tubing: <strong>{r.tubingMeters}m</strong></span>}
        </div>
      </div>
      <div style={{ fontSize: 12 }}>
        {r.items.map((item, i) => (
          <div key={i} style={{ display: "flex", gap: 8, padding: "3px 0", borderBottom: i < r.items.length - 1 ? `1px solid ${C.border}` : "none" }}>
            <span style={{ width: 180, fontFamily: "monospace", color: C.info, fontWeight: 600, fontSize: 11 }}>{item.pn}</span>
            <span style={{ flex: 1, color: C.t2 }}>{item.desc}</span>
            <span style={{ width: 30, textAlign: "center", fontWeight: 600 }}>{item.qty}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AggregatedBOMTable({ bom }) {
  return (
    <div style={{ marginTop: 20 }}>
      <h4 style={{ fontSize: 15, fontWeight: 700, color: C.navy, margin: "0 0 12px" }}>Aggregated Project BOM — Order Summary</h4>
      <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "200px 1fr 70px", padding: "10px 16px", background: C.navy, color: C.white, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          <span>Part Number</span><span>Description</span><span style={{ textAlign: "center" }}>Qty</span>
        </div>
        {bom.map((row, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "200px 1fr 70px", padding: "10px 16px", borderBottom: i < bom.length - 1 ? `1px solid ${C.border}` : "none", fontSize: 13, alignItems: "center", background: i % 2 === 0 ? C.white : C.grayBg }}>
            <span style={{ fontWeight: 600, color: C.info, fontFamily: "monospace", fontSize: 12 }}>{row.pn}</span>
            <span style={{ color: C.t1 }}>{row.desc}</span>
            <span style={{ textAlign: "center", fontWeight: 600, color: C.t1 }}>{row.qty}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SuccessModal({ onExit, onAnother }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: C.white, borderRadius: 16, padding: "40px 36px", maxWidth: 420, textAlign: "center", boxShadow: "0 12px 48px rgba(0,0,0,0.15)" }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#D1FAE5", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 32 }}>✓</div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: C.t1, margin: "0 0 8px" }}>Submitted for Quoting</h2>
        <p style={{ fontSize: 14, color: C.t2, margin: "0 0 4px" }}>Your estimation has been submitted for quoting and is now pending review.</p>
        <p style={{ fontSize: 13, color: C.t3, fontStyle: "italic", margin: "0 0 28px" }}>Su estimación ha sido enviada para cotización y está pendiente de revisión.</p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <button onClick={onExit} style={{ padding: "12px 28px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.white, color: C.t1, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Exit</button>
          <button onClick={onAnother} style={{ padding: "12px 28px", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${C.navy} 0%,${C.navyLight} 100%)`, color: C.white, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Submit Another</button>
        </div>
      </div>
    </div>
  );
}

const MAX_KITCHENS = 5;
const makeKitchen = (i) => ({ id: i, name: `Kitchen ${i}`, cookL: "", cookW: "", duct1L: "", duct1W: "", duct2L: "", duct2W: "" });

export default function Eco7KitchenCalc({ user, onBack, onSubmitRequest, onExit, onUnsavedChange }) {
  const [form, setForm] = useState({
    projectName: "", projectLocation: "", country: user.country || "",
    designerName: user.name || "", email: user.email || "", phone: "", notes: "",
    unitSystem: "", detectionType: "", gasValve: "", gasValveDiameter: "",
    pressureSupervisor: "", supervisableSolenoid: "", manualRelease: "",
  });
  const [kitchens, setKitchens] = useState([makeKitchen(1)]);
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showAbandon, setShowAbandon] = useState(false);
  const [dirty, setDirty] = useState(false);
  const resultRef = useRef(null);

  const isMetric = form.unitSystem.startsWith("Metric");
  const isThermal = form.detectionType === "Thermal Tubing";
  const isElectronic = form.detectionType === "Electronic Heat Detector";
  const unitLabel = isMetric ? "m" : "ft";

  const set = (key, val) => {
    setDirty(true);
    if (onUnsavedChange) onUnsavedChange(true);
    setForm(prev => {
      const next = { ...prev, [key]: val };
      if (key === "detectionType") {
        next.supervisableSolenoid = "";
        next.manualRelease = val === "Electronic Heat Detector" ? "Manual Pull Station" : "";
      }
      if (key === "gasValve" && val !== "Yes") next.gasValveDiameter = "";
      return next;
    });
    if (errors[key]) setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const setKitch = (idx, key, val) => {
    setDirty(true);
    if (onUnsavedChange) onUnsavedChange(true);
    setKitchens(prev => prev.map((k, i) => i === idx ? { ...k, [key]: val } : k));
  };

  const addKitchen = () => {
    if (kitchens.length >= MAX_KITCHENS) return;
    setKitchens(prev => [...prev, makeKitchen(prev.length + 1)]);
  };

  const removeKitchen = (idx) => {
    if (kitchens.length <= 1) return;
    setKitchens(prev => prev.filter((_, i) => i !== idx).map((k, i) => ({ ...k, id: i + 1, name: `Kitchen ${i + 1}` })));
  };

  const validate = () => {
    const e = {};
    const req = (k, msg) => { if (!form[k]?.trim()) e[k] = msg || "Required"; };
    req("projectName"); req("projectLocation"); req("unitSystem");
    req("detectionType"); req("gasValve"); req("pressureSupervisor"); req("manualRelease");
    if (form.gasValve === "Yes") req("gasValveDiameter", "Required when gas valve is selected");
    if (isElectronic) req("supervisableSolenoid");
    if (!kitchens[0].cookL || !kitchens[0].cookW) e.kitchen1 = "Kitchen 1 cooking surface dimensions are required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleCalculate = () => {
    if (!validate()) return;
    setCalculating(true);
    setTimeout(() => {
      const config = {
        detectionType: form.detectionType,
        gasValve: form.gasValve === "Yes",
        gasValveDiameter: form.gasValveDiameter,
        pressureSupervisor: form.pressureSupervisor === "Yes",
        supervisableSolenoid: form.supervisableSolenoid,
        manualRelease: form.manualRelease,
      };
      const res = generateEco7BOM(kitchens, config, isMetric);
      setResult(res);
      setCalculating(false);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }, 400);
  };

  const handleSubmitForQuoting = async () => {
    if (!result || result.error || submitting) return;
    setSubmitting(true);
    try {
      const pdf = generateEco7ReportBase64({ ...form, company: user.company }, result, isMetric);
      const success = await onSubmitRequest({ ...form, solutionType: "System Estimation: Eco7 Kitchen Suppression", submittedBy: user.name }, [{ name: pdf.fileName, mimeType: pdf.mimeType, base64: pdf.base64 }]);
      if (success) setShowSuccess(true);
    } catch (err) { console.error(err); alert("Submission failed."); }
    setSubmitting(false);
  };

  const handleReset = () => {
    setForm({ projectName: "", projectLocation: "", country: user.country || "", designerName: user.name || "", email: user.email || "", phone: "", notes: "", unitSystem: "", detectionType: "", gasValve: "", gasValveDiameter: "", pressureSupervisor: "", supervisableSolenoid: "", manualRelease: "" });
    setKitchens([makeKitchen(1)]); setResult(null); setErrors({}); setShowSuccess(false); setDirty(false);
    if (onUnsavedChange) onUnsavedChange(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => { if (dirty) setShowAbandon(true); else onBack(); };

  return (
    <div style={{ maxWidth: 900 }}>
      {showSuccess && <SuccessModal onExit={onExit || onBack} onAnother={handleReset} />}
      {showAbandon && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: C.white, borderRadius: 16, padding: "32px 36px", maxWidth: 400, textAlign: "center", boxShadow: "0 12px 48px rgba(0,0,0,0.15)" }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: C.t1, margin: "0 0 8px" }}>Unsaved Changes</h3>
            <p style={{ fontSize: 14, color: C.t2, margin: "0 0 24px" }}>You have unsaved changes. Are you sure you want to leave?</p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button onClick={() => setShowAbandon(false)} style={{ padding: "10px 24px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.white, color: C.t1, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>Continue Editing</button>
              <button onClick={onBack} style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: C.red, color: C.white, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Abandon</button>
            </div>
          </div>
        </div>
      )}

      <button onClick={handleBack} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: C.t3, fontSize: 13, fontFamily: "inherit", padding: "4px 0", marginBottom: 16 }}
        onMouseEnter={e => e.currentTarget.style.color = C.navy} onMouseLeave={e => e.currentTarget.style.color = C.t3}>
        <Ic.ChevronLeft /> Back to Estimation Tools
      </button>

      <div style={{ background: C.white, borderRadius: 16, border: `1px solid ${C.border}`, padding: "32px 36px", boxShadow: "0 2px 8px rgba(0,0,0,0.03)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 8 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "#F59E0B12", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🍳</div>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: C.t1, margin: 0 }}>Eco7 Kitchen Suppression Calculator</h2>
            <p style={{ fontSize: 13, color: C.t2, margin: 0 }}>Chemori Eco7 — Multi-Kitchen</p>
          </div>
        </div>
        <p style={{ fontSize: 11, color: C.t3, margin: "0 0 24px" }}><span style={{ color: C.red }}>*</span> Required fields / Campos obligatorios</p>

        <Section title="Project Information" titleEs="Información del Proyecto" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
          <TF en="Project Name" es="Nombre del Proyecto" value={form.projectName} onChange={v => set("projectName", v)} required error={errors.projectName} />
          <TF en="Project Location" es="Ubicación del Proyecto" value={form.projectLocation} onChange={v => set("projectLocation", v)} required error={errors.projectLocation} />
          <TF en="Country" es="País" value={form.country} onChange={v => set("country", v)} />
          <TF en="Designer Name" es="Nombre del Diseñador" value={form.designerName} onChange={v => set("designerName", v)} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
          <TF en="Email" value={form.email} onChange={v => set("email", v)} />
          <TF en="Phone" value={form.phone} onChange={v => set("phone", v)} />
        </div>
        <TF en="Additional Notes" es="Notas Adicionales" value={form.notes} onChange={v => set("notes", v)} placeholder="Additional notes..." rows={3} />

        <Section title="System Configuration" titleEs="Configuración del Sistema" />
        <p style={{ fontSize: 12, color: C.t3, margin: "-12px 0 16px", fontStyle: "italic" }}>
          These settings apply to all kitchens in this project. / Estas configuraciones aplican a todas las cocinas del proyecto.
        </p>

        <UnitSelector value={form.unitSystem} onChange={v => set("unitSystem", v)} required error={errors.unitSystem} />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
          <SF en="Detection Type" es="Tipo de Detección" value={form.detectionType}
            onChange={v => set("detectionType", v)} options={["Thermal Tubing", "Electronic Heat Detector"]}
            required error={errors.detectionType} />

          <SF en="Pressure Supervisor" es="Supervisor de Presión" value={form.pressureSupervisor}
            onChange={v => set("pressureSupervisor", v)} options={["Yes", "No"]}
            required error={errors.pressureSupervisor} />

          <SF en="Gas Shut-off Valve?" es="¿Válvula de Corte de Gas?" value={form.gasValve}
            onChange={v => set("gasValve", v)} options={["Yes", "No"]}
            required error={errors.gasValve} />

          {form.gasValve === "Yes" && (
            <SF en="Gas Valve Diameter" es="Diámetro de la Válvula" value={form.gasValveDiameter}
              onChange={v => set("gasValveDiameter", v)} options={['1/2"', '3/4"', '1"']}
              required error={errors.gasValveDiameter} />
          )}

          <SF en="Supervisable Solenoid" es="Solenoide Supervisable" value={form.supervisableSolenoid}
            onChange={v => set("supervisableSolenoid", v)} options={["Yes", "No"]}
            required={isElectronic} error={errors.supervisableSolenoid}
            disabled={isThermal} note={isThermal ? "Not applicable for Thermal Tubing" : ""} />

          <SF en="Manual Release" es="Liberación Manual" value={form.manualRelease}
            onChange={v => set("manualRelease", v)}
            options={isElectronic ? ["Manual Pull Station"] : ["Wall Mounted", "Cabinet Mounted"]}
            required error={errors.manualRelease}
            disabled={isElectronic} note={isElectronic ? "Locked to Manual Pull Station for Electronic Heat Detection" : ""} />
        </div>

        {isElectronic && (
          <div style={{ padding: "10px 14px", borderRadius: 8, background: "#EFF6FF", border: "1px solid #BFDBFE", marginBottom: 16 }}>
            <p style={{ fontSize: 12, color: "#1E40AF", margin: 0 }}>
              <strong>Electronic Heat Detection:</strong> Only available for kitchens requiring ≥8L agent (35LB+ cylinder). A fire control panel and strobe horn will be included per kitchen. Manual release is locked to Manual Pull Station.
            </p>
          </div>
        )}

        <Section title="Kitchen & Hood Dimensions" titleEs="Dimensiones de Cocina y Campana" />
        <p style={{ fontSize: 12, color: C.t3, margin: "-12px 0 16px", fontStyle: "italic" }}>
          Enter dimensions for each kitchen. Cooking surface is required. Exhaust duct dimensions are optional (up to 2 ducts per kitchen).
        </p>

        {kitchens.map((k, idx) => (
          <div key={k.id} style={{ marginBottom: 20, padding: 16, borderRadius: 12, border: `1px solid ${C.border}`, background: idx % 2 === 0 ? C.white : C.grayBg }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h4 style={{ fontSize: 14, fontWeight: 700, color: C.navy, margin: 0 }}>
                Kitchen {idx + 1}{idx === 0 && <span style={{ color: C.red }}> *</span>}
              </h4>
              {idx > 0 && (
                <button onClick={() => removeKitchen(idx)} style={{ background: "none", border: "none", cursor: "pointer", color: C.t3, fontSize: 12, fontFamily: "inherit" }}
                  onMouseEnter={e => e.currentTarget.style.color = C.red} onMouseLeave={e => e.currentTarget.style.color = C.t3}>
                  ✕ Remove
                </button>
              )}
            </div>

            <h5 style={{ fontSize: 12, fontWeight: 600, color: C.t2, margin: "0 0 8px" }}>
              Cooking Surface / Hood ({unitLabel}) <span style={{ color: C.red }}>*</span>
            </h5>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
              <TF en="Length" es="Largo" value={k.cookL} onChange={v => setKitch(idx, "cookL", v)} required={idx === 0} error={idx === 0 ? errors.kitchen1 : undefined} />
              <TF en="Width" es="Ancho" value={k.cookW} onChange={v => setKitch(idx, "cookW", v)} />
            </div>

            <h5 style={{ fontSize: 12, fontWeight: 600, color: C.t2, margin: "4px 0 8px" }}>
              Exhaust Duct 1 ({unitLabel}) <span style={{ fontSize: 11, fontWeight: 400, color: C.t3 }}>(optional)</span>
            </h5>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
              <TF en="Length" es="Largo" value={k.duct1L} onChange={v => setKitch(idx, "duct1L", v)} />
              <TF en="Width" es="Ancho" value={k.duct1W} onChange={v => setKitch(idx, "duct1W", v)} />
            </div>

            <h5 style={{ fontSize: 12, fontWeight: 600, color: C.t2, margin: "4px 0 8px" }}>
              Exhaust Duct 2 ({unitLabel}) <span style={{ fontSize: 11, fontWeight: 400, color: C.t3 }}>(optional)</span>
            </h5>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
              <TF en="Length" es="Largo" value={k.duct2L} onChange={v => setKitch(idx, "duct2L", v)} />
              <TF en="Width" es="Ancho" value={k.duct2W} onChange={v => setKitch(idx, "duct2W", v)} />
            </div>
          </div>
        ))}

        {kitchens.length < MAX_KITCHENS && (
          <button onClick={addKitchen} style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 20px", borderRadius: 10, border: `1.5px dashed ${C.navy}40`, background: `${C.navy}05`, color: C.navy, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", width: "100%", justifyContent: "center", transition: "all 0.2s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.navy; e.currentTarget.style.background = `${C.navy}10`; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = `${C.navy}40`; e.currentTarget.style.background = `${C.navy}05`; }}>
            + Add Kitchen ({kitchens.length}/{MAX_KITCHENS})
          </button>
        )}

        {Object.keys(errors).length > 0 && (
          <div style={{ marginTop: 24, padding: "12px 16px", borderRadius: 10, background: "#FEF2F2", border: "1px solid #FECACA" }}>
            <p style={{ fontSize: 13, color: C.red, fontWeight: 600, margin: 0 }}>Please complete all required fields before calculating.</p>
          </div>
        )}

        <div style={{ marginTop: 28, display: "flex", justifyContent: "center" }}>
          <button onClick={handleCalculate} disabled={calculating}
            style={{ padding: "14px 48px", borderRadius: 12, border: "none", background: `linear-gradient(135deg,${C.navy} 0%,${C.navyLight} 100%)`, color: C.white, fontSize: 16, fontWeight: 700, cursor: calculating ? "not-allowed" : "pointer", fontFamily: "inherit", boxShadow: `0 6px 24px ${C.navy}30`, opacity: calculating ? 0.75 : 1 }}>
            {calculating ? "Calculating..." : "CALCULATE / CALCULAR"}
          </button>
        </div>
      </div>

      {result && (
        <div ref={resultRef} style={{ marginTop: 24 }}>
          {result.error ? (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 12, padding: "20px 24px" }}>
              <p style={{ fontSize: 15, color: C.red, fontWeight: 600, margin: 0 }}>{result.error}</p>
            </div>
          ) : (
            <div style={{ background: C.white, borderRadius: 16, border: `1px solid ${C.border}`, padding: "28px 36px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 24 }}>
                <div style={{ background: `${C.navy}08`, borderRadius: 10, padding: "14px 16px", border: `1px solid ${C.navy}15` }}>
                  <div style={{ fontSize: 11, color: C.t3, textTransform: "uppercase" }}>Total Eco7 Required</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: C.navy }}>{result.totalAgentLiters} L</div>
                </div>
                <div style={{ background: `${C.navy}08`, borderRadius: 10, padding: "14px 16px", border: `1px solid ${C.navy}15` }}>
                  <div style={{ fontSize: 11, color: C.t3, textTransform: "uppercase" }}>Total Nozzles</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: C.navy }}>{result.totalNozzles}</div>
                </div>
                <div style={{ background: `${C.navy}08`, borderRadius: 10, padding: "14px 16px", border: `1px solid ${C.navy}15` }}>
                  <div style={{ fontSize: 11, color: C.t3, textTransform: "uppercase" }}>Kitchens</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: C.navy }}>{result.kitchenResults.length}</div>
                </div>
              </div>

              <h3 style={{ fontSize: 16, fontWeight: 700, color: C.navy, margin: "0 0 12px" }}>Per-Kitchen Breakdown</h3>
              {result.kitchenResults.map((r, i) => <KitchenBOMTable key={i} r={r} />)}
              <AggregatedBOMTable bom={result.aggregatedBom} />

              <div style={{ marginTop: 16, padding: "10px 14px", borderRadius: 8, background: "#FEF3C7", border: "1px solid #FDE68A" }}>
                <p style={{ fontSize: 12, color: "#92400E", margin: 0 }}>
                  <strong>Note:</strong> Always verify the system configuration with engineering. This is a preliminary estimate for budgeting purposes only.
                </p>
              </div>

              <div style={{ marginTop: 24, display: "flex", gap: 12, justifyContent: "center" }}>
                <button onClick={handleSubmitForQuoting} disabled={submitting}
                  style={{ padding: "12px 28px", borderRadius: 10, border: `1px solid ${C.border}`, background: C.white, color: C.t1, fontSize: 14, cursor: submitting ? "not-allowed" : "pointer", fontFamily: "inherit", fontWeight: 500, opacity: submitting ? 0.7 : 1 }}>
                  {submitting ? "Submitting..." : "Submit for Quoting"}
                </button>
                <button onClick={() => generateEco7Report({ ...form, company: user.company }, result, isMetric)}
                  style={{ padding: "12px 28px", borderRadius: 10, border: "none", background: `linear-gradient(135deg,${C.navy} 0%,${C.navyLight} 100%)`, color: C.white, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", boxShadow: `0 4px 16px ${C.navy}25` }}>
                  Generate PDF Report
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
